// Acknowledgements / data-sources page. Lists every upstream data provider,
// what ParlSearch uses from them, and their copyright / licensing, with links
// to each provider's authoritative statement. Linked from the site footer.

import React from 'react';
import {
  Box, Paper, Typography, Link, Divider, Chip, Stack, Alert,
} from '@mui/material';

const SOURCES = [
  {
    name: 'Australian Parliament House (APH)',
    site: 'https://www.aph.gov.au',
    used: 'The bulk of ParlSearch. Parliamentary Handbook (parliamentarians, parties, electorates, parliaments, ministries, committees, elections, referendums, Royal Commissions, heads of state, casual vacancies, the sitting calendar); ParlInfo (Hansard, House Votes & Proceedings, Senate Journals, Notice Papers, tabled documents, divisions, questions); ParlWork (bills, questions on notice); ParlView (broadcast video); petitions, notices of motion and parliamentarian photos.',
    endpoints: ['handbook.aph.gov.au', 'parlinfo.aph.gov.au', 'parlwork.aph.gov.au', 'aph.gov.au'],
    licence: '© Commonwealth of Australia. Parliamentary material is generally made available under a Creative Commons Attribution-NonCommercial-NoDerivs (CC BY-NC-ND) licence, except where otherwise noted; some content is © Commonwealth of Australia and reproduced by permission.',
    licenceUrl: 'https://www.aph.gov.au/Help/Disclaimer_Privacy_Copyright',
    licenceLabel: 'APH copyright & licence',
  },
  {
    name: 'Australian Electoral Commission (AEC)',
    site: 'https://www.aec.gov.au',
    used: 'Federal electoral division boundaries and redistributions used to render the electorate maps.',
    endpoints: ['aec.gov.au'],
    licence: '© Commonwealth of Australia (Australian Electoral Commission). AEC data is generally made available under a Creative Commons Attribution 4.0 (CC BY 4.0) licence.',
    licenceUrl: 'https://www.aec.gov.au/footer/copyright.htm',
    licenceLabel: 'AEC copyright & licence',
  },
  {
    name: 'Department of the House of Representatives — HouseLive',
    site: 'https://houselive.houseofrepresentatives.gov.au',
    used: 'Question Time questions and answers with caption transcripts, and live (draft) chamber minutes.',
    endpoints: ['houseofrepresentatives.gov.au'],
    licence: '© Commonwealth of Australia (Department of the House of Representatives). Parliamentary broadcast and proceedings are subject to the conditions for use of parliamentary material.',
    licenceUrl: 'https://www.aph.gov.au/Help/Disclaimer_Privacy_Copyright',
    licenceLabel: 'Conditions of use',
  },
  {
    name: 'National Archives of Australia (NAA)',
    site: 'https://www.naa.gov.au',
    used: 'Deep links to archived Royal Commission reports (RecordSearch). ParlSearch links to these records; it does not host them.',
    endpoints: ['recordsearch.naa.gov.au'],
    licence: '© Commonwealth of Australia (National Archives of Australia). Records are linked to at their source.',
    licenceUrl: 'https://www.naa.gov.au/copyright',
    licenceLabel: 'NAA copyright',
  },
];

export default function Acknowledgements() {
  return (
    <Box sx={{ maxWidth: 920, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ mb: 1 }}>Acknowledgements &amp; data sources</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        ParlSearch is built on open Australian parliamentary and electoral data. The
        data is sourced from the official providers below and remains their copyright;
        ParlSearch only indexes and presents it. Where a provider publishes a licence,
        it is noted — please consult each provider's statement for the authoritative terms.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        ParlSearch is an independent, non-commercial project. It is not affiliated with,
        endorsed by, or an official service of the Australian Parliament, the Department
        of the House of Representatives, the Australian Electoral Commission, or any other
        body listed here. Data may contain errors or omissions — always confirm against
        the authoritative source.
      </Alert>

      <Stack spacing={2}>
        {SOURCES.map((s) => (
          <Paper key={s.name} elevation={1} sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              <Link href={s.site} target="_blank" rel="noopener noreferrer" underline="hover">
                {s.name}
              </Link>
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
              {s.endpoints.map((e) => (
                <Chip key={e} label={e} size="small" variant="outlined" />
              ))}
            </Stack>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>Used for:</strong> {s.used}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {s.licence}
            </Typography>
            <Link href={s.licenceUrl} target="_blank" rel="noopener noreferrer" underline="hover">
              {s.licenceLabel} ↗
            </Link>
          </Paper>
        ))}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        If you are a data owner and have a concern about how your material is presented
        here, please get in touch via the feedback option in the site menu.
      </Typography>
    </Box>
  );
}
